import React, { useEffect, useMemo, useRef, useState, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { Action, ActionStats, ActionTypeFilter } from '../types/Action';
import { actionsService, parseDependentsCount, formatDependentsCount } from '../services/actionsService';
import { normalizeRepoName, matchesSearchQuery, isActionVerified, isGitHubOwnedAction } from '../services/utils';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { NavBar } from '../components/NavBar';
import { GitHubOwnedBadge } from '../components/GitHubOwnedBadge';

const PAGE_SIZE = 12;
const OVERVIEW_STATE_KEY = 'overviewState:v1';

// Thresholds for the low-activity filter:
// an action is considered low-activity when BOTH conditions are true.
const LOW_ACTIVITY_DEPENDENTS_THRESHOLD = 10;
const LOW_ACTIVITY_STALE_DAYS = 30;

type OverviewUiState = {
  searchQuery: string;
  typeFilter: ActionTypeFilter;
  showVerifiedOnly: boolean;
  verifiedFilter?: 'all' | 'verified' | 'unverified';
  archivedFilter?: 'hide' | 'show' | 'only';
  activityFilter?: 'hide' | 'show' | 'only';
  sortBy: 'updated' | 'dependents';
  currentPage: number;
  scrollY?: number;
  openssfFilter?: 'all' | 'above5' | 'above7';
  githubOwnedFilter?: 'all' | 'only' | 'hide';
};

function readOverviewState(): Partial<OverviewUiState> | null {
  try {
    const raw = sessionStorage.getItem(OVERVIEW_STATE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeOverviewState(state: OverviewUiState) {
  try {
    sessionStorage.setItem(OVERVIEW_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export const OverviewPage: React.FC = () => {
  const initialPersisted = readOverviewState();
  const initialActions = actionsService.getActions();
  const initialStats = actionsService.getStats();

  const [actions, setActions] = useState<Action[]>(initialActions);
  const [searchQuery, setSearchQuery] = useState(() => (initialPersisted?.searchQuery ?? ''));
  // Defer search query so each keystroke does not trigger a synchronous filter pass
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [typeFilter, setTypeFilter] = useState<ActionTypeFilter>(() => {
    const candidate = initialPersisted?.typeFilter;
    const supported: ActionTypeFilter[] = ['All', 'Node', 'Docker', 'Composite', 'Unknown', 'No file found'];
    return candidate && supported.includes(candidate) ? candidate : 'All';
  });
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(() => Boolean(initialPersisted?.showVerifiedOnly));
  const [verifiedFilter, setVerifiedFilter] = useState<'all' | 'verified' | 'unverified'>(() => {
    const persisted = (initialPersisted as any)?.verifiedFilter as 'all' | 'verified' | 'unverified' | undefined;
    return persisted || 'all';
  });
  const [archivedFilter, setArchivedFilter] = useState<'hide' | 'show' | 'only'>(() => {
    const persisted = (initialPersisted as any)?.archivedFilter as 'hide' | 'show' | 'only' | undefined;
    return persisted || 'hide';
  });
  const [activityFilter, setActivityFilter] = useState<'hide' | 'show' | 'only'>(() => {
    const persisted = (initialPersisted as any)?.activityFilter as 'hide' | 'show' | 'only' | undefined;
    return persisted || 'hide';
  });
  const [openssfFilter, setOpenssfFilter] = useState<'all' | 'above5' | 'above7'>(() => {
    const candidate = initialPersisted?.openssfFilter as string | undefined;
    return candidate === 'above5' || candidate === 'above7' ? candidate : 'all';
  });
  const [githubOwnedFilter, setGithubOwnedFilter] = useState<'all' | 'only' | 'hide'>(() => {
    const candidate = (initialPersisted as any)?.githubOwnedFilter as string | undefined;
    return candidate === 'only' || candidate === 'hide' ? candidate : 'all';
  });
  const [sortBy, setSortBy] = useState<'updated' | 'dependents'>(() => (initialPersisted?.sortBy === 'dependents' ? 'dependents' : 'updated'));
  const [currentPage, setCurrentPage] = useState(() => {
    const candidate = Number(initialPersisted?.currentPage);
    return Number.isFinite(candidate) && candidate > 0 ? candidate : 1;
  });
  const [loading, setLoading] = useState(() => initialActions.length === 0 && initialStats.total === 0);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ActionStats>(initialStats);
  const navigate = useNavigate();

  const prevFiltersRef = useRef({
    searchQuery: deferredSearchQuery,
    typeFilter,
    showVerifiedOnly,
    archivedFilter,
    activityFilter,
    verifiedFilter,
    sortBy,
    openssfFilter,
    githubOwnedFilter
  });
  const restoredScrollRef = useRef(false);

  const setTypeFilterFromStats = (type: string) => {
    if (type === 'All') {
      setTypeFilter('All');
      return;
    }

    if (type === 'Verified') {
      setVerifiedFilter('verified');
      return;
    }

    if (type === 'Archived') {
      setArchivedFilter('only');
      return;
    }

    const supportedTypes: ActionTypeFilter[] = ['Node', 'Docker', 'Composite', 'Unknown', 'No file found'];
    if (supportedTypes.includes(type as ActionTypeFilter)) {
      setTypeFilter(type as ActionTypeFilter);
      return;
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('All');
    setShowVerifiedOnly(false);
    setArchivedFilter('hide');
    setActivityFilter('hide');
    setOpenssfFilter('all');
    setSortBy('updated');
    setGithubOwnedFilter('all');
    setCurrentPage(1);
    try {
      sessionStorage.removeItem(OVERVIEW_STATE_KEY);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const unsubscribe = actionsService.subscribe(() => {
      setActions(actionsService.getActions());
      setStats(actionsService.getStats());
    });

    loadData();

    return () => {
      unsubscribe();
    };
  }, []);

  const loadData = async (options?: { retries?: number }) => {
    // Cold starts + large payloads can take a while; retry a bit longer before showing an error.
    const retries = Math.max(0, options?.retries ?? 5);
    const hadNoData = actionsService.getActions().length === 0 && actionsService.getStats().total === 0;

    if (hadNoData) {
      setLoading(true);
    }

    let lastErr: unknown;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        const force = attempt > 0;

        // One request for the whole dataset. This used to show a 50-row
        // preview first and swap in the full list once the ~50s scan finished,
        // but that preview was the first 50 rows in PartitionKey order — owners
        // starting with "0" — of which only a handful survived the default
        // filters. The snapshot arrives fast enough and pre-sorted, so the
        // first render is already the real, correctly ordered result.
        const [statsData, allActions] = await Promise.all([
          actionsService.fetchStats(force),
          actionsService.fetchActions(force)
        ]);

        setStats(statsData);
        setActions(allActions);
        setError(null);
        setLoading(false);

        return;
      } catch (err) {
        lastErr = err;
        if (attempt >= retries) {
          break;
        }
        const delayMs = Math.min(2500 * (attempt + 1), 15000);
        console.warn(`Overview load failed (attempt ${attempt + 1}/${retries + 1}); retrying in ${delayMs}ms`, err);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }

    setError('Failed to load actions. Please try again later.');
    console.error(lastErr);
    setLoading(false);
  };

  useEffect(() => {
    writeOverviewState({
      searchQuery,
      typeFilter,
      showVerifiedOnly,
      verifiedFilter,
      archivedFilter,
      activityFilter,
      sortBy,
      openssfFilter,
      githubOwnedFilter,
      currentPage
    });
  }, [searchQuery, typeFilter, showVerifiedOnly, verifiedFilter, archivedFilter, activityFilter, sortBy, openssfFilter, githubOwnedFilter, currentPage]);

  // Memoize filter+sort to avoid recomputing on every render.
  // Uses deferredSearchQuery so typing does not block the UI.
  const filteredActions = useMemo(() => {
    let filtered = actions;

    const normalizedQuery = deferredSearchQuery.trim();
    if (normalizedQuery) {
      filtered = filtered.filter(action => matchesSearchQuery({
        owner: action.owner,
        name: action.name,
        actionType: action.actionType?.actionType,
        verified: isActionVerified(action),
        archived: action.repoInfo?.archived,
        description: action.description
      }, normalizedQuery));
    }

    if (typeFilter !== 'All') {
      filtered = filtered.filter(
        action => action?.actionType?.actionType === typeFilter
      );
    }
    if (verifiedFilter === 'verified') {
      filtered = filtered.filter(action => isActionVerified(action));
    } else if (verifiedFilter === 'unverified') {
      filtered = filtered.filter(action => !isActionVerified(action));
    }

    if (archivedFilter === 'only') {
      filtered = filtered.filter(action => action?.repoInfo?.archived === true);
    } else if (archivedFilter === 'hide') {
      filtered = filtered.filter(action => action?.repoInfo?.archived !== true);
    }

    // Filter by low-activity: dependents below threshold AND not updated within the stale window.
    if (activityFilter !== 'show') {
      const staleDate = new Date();
      staleDate.setDate(staleDate.getDate() - LOW_ACTIVITY_STALE_DAYS);
      const isLowActivity = (action: Action) => {
        const deps = parseDependentsCount(action?.dependents?.dependents);
        const updatedAt = new Date(action?.repoInfo?.updated_at || 0);
        return deps < LOW_ACTIVITY_DEPENDENTS_THRESHOLD && updatedAt < staleDate;
      };
      if (activityFilter === 'only') {
        filtered = filtered.filter(isLowActivity);
      } else {
        filtered = filtered.filter(action => !isLowActivity(action));
      }
    }

    // Filter by OpenSSF score threshold
    if (openssfFilter && openssfFilter !== 'all') {
      const threshold = openssfFilter === 'above7' ? 7 : 5;
      filtered = filtered.filter(action => {
        const score = typeof action.ossfScore === 'number' ? action.ossfScore : -1;
        return score > threshold;
      });
    }

    if (githubOwnedFilter === 'only') {
      filtered = filtered.filter(action => isGitHubOwnedAction(action));
    } else if (githubOwnedFilter === 'hide') {
      filtered = filtered.filter(action => !isGitHubOwnedAction(action));
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      if (sortBy === 'dependents') {
        const aDeps = parseDependentsCount(a?.dependents?.dependents);
        const bDeps = parseDependentsCount(b?.dependents?.dependents);
        return bDeps - aDeps; // Descending
      } else {
        // Sort by updated date
        const aDate = new Date(a?.repoInfo?.updated_at || 0).getTime();
        const bDate = new Date(b?.repoInfo?.updated_at || 0).getTime();
        return bDate - aDate; // Descending (most recent first)
      }
    });
  }, [actions, deferredSearchQuery, typeFilter, showVerifiedOnly, archivedFilter, activityFilter, verifiedFilter, sortBy, openssfFilter, githubOwnedFilter]);

  // Reset page to 1 when filter criteria change; clamp when only the actions list updates.
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.searchQuery !== deferredSearchQuery ||
      prev.typeFilter !== typeFilter ||
      prev.showVerifiedOnly !== showVerifiedOnly ||
      prev.verifiedFilter !== verifiedFilter ||
      prev.archivedFilter !== archivedFilter ||
      prev.activityFilter !== activityFilter ||
      prev.sortBy !== sortBy ||
      prev.openssfFilter !== openssfFilter ||
      prev.githubOwnedFilter !== githubOwnedFilter;

    prevFiltersRef.current = { searchQuery: deferredSearchQuery, typeFilter, showVerifiedOnly, archivedFilter, activityFilter, verifiedFilter, sortBy, openssfFilter, githubOwnedFilter };

    const nextTotalPages = Math.max(1, Math.ceil(filteredActions.length / PAGE_SIZE));
    if (filtersChanged) {
      setCurrentPage(1);
    } else {
      setCurrentPage(p => Math.min(Math.max(p, 1), nextTotalPages));
    }
  }, [filteredActions, deferredSearchQuery, typeFilter, showVerifiedOnly, archivedFilter, activityFilter, verifiedFilter, sortBy, openssfFilter, githubOwnedFilter]);

  useEffect(() => {
    if (restoredScrollRef.current) {
      return;
    }

    if (loading) {
      return;
    }

    const persisted = readOverviewState();
    const scrollY = Number(persisted?.scrollY);
    if (!Number.isFinite(scrollY) || scrollY <= 0) {
      restoredScrollRef.current = true;
      return;
    }

    restoredScrollRef.current = true;
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }, [loading]);

  const totalPages = Math.max(1, Math.ceil(filteredActions.length / PAGE_SIZE));
  const pagedActions = filteredActions.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const goToPage = (page: number) => {
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(nextPage);
  };

  const showingFrom = filteredActions.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(currentPage * PAGE_SIZE, filteredActions.length);

  // The stat tiles report database totals while the grid reports what survives
  // the filters, and the defaults (hide archived, hide low-activity) remove
  // roughly 70% of the dataset. Without spelling that out, the two numbers just
  // look like they disagree.
  const hiddenByFilters = Math.max(0, actions.length - filteredActions.length);
  const hasActiveFilters = hiddenByFilters > 0;

  const handleActionClick = (action: Action, event?: React.MouseEvent) => {
    const detailUrl = `/action/${encodeURIComponent(action.owner)}/${encodeURIComponent(action.name)}`;

    if (event) {
      const isModifiedClick = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0;
      if (isModifiedClick) {
        // Let the browser open the <a href> in a new tab/window.
        return;
      }
      event.preventDefault();
    }

    writeOverviewState({
      searchQuery,
      typeFilter,
      showVerifiedOnly,
      archivedFilter,
      activityFilter,
      sortBy,
      currentPage,
      scrollY: window.scrollY
    });
    navigate(detailUrl);
  };

  const getActionTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'Node':
        return 'badge-node';
      case 'Docker':
        return 'badge-docker';
      case 'Composite':
        return 'badge-composite';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading">Loading actions...</div>
      </div>
    );
  }

  if (error) {
    const empty = actions.length === 0 && stats.total === 0;
    return (
      <div className="app">
        <div className="error-message">{error}</div>
        {empty && (
          <button type="button" onClick={() => loadData()} style={{ marginTop: 12 }}>
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <NavBar />
        <h1>Alternative GitHub Actions Marketplace</h1>
        <p>Browse and search through <AnimatedCounter value={stats.total} /> with more information</p>
      </div>

      {/* These tiles count the whole database. The grid below counts only what
          passes the current filters, so the two differ by design — the
          pagination summary spells out the gap. */}
      <div className="stats-bar" aria-label="Database totals across all actions">
        <button
          type="button"
          className="stat-item stat-button"
          onClick={() => setTypeFilterFromStats('All')}
          aria-label="Show all actions"
        >
          <span className="stat-label">Total</span>
          <span className="stat-value"><AnimatedCounter value={stats.total} /></span>
        </button>

        <button
          type="button"
          className="stat-item stat-button"
          onClick={() => setTypeFilterFromStats('Verified')}
          aria-label="Show verified actions"
        >
          <span className="stat-label">Verified</span>
          <span className="stat-value"><AnimatedCounter value={stats.verified} /></span>
        </button>

        {Object.entries(stats.byType).map(([type, count]) => (
          <button
            key={type}
            type="button"
            className="stat-item stat-button"
            onClick={() => setTypeFilterFromStats(type)}
            aria-label={`Filter by ${type} actions`}
          >
            <span className="stat-label">{type}</span>
            <span className="stat-value"><AnimatedCounter value={count} /></span>
          </button>
        ))}
        
        <button
          type="button"
          className="stat-item stat-button"
          onClick={() => setTypeFilterFromStats('Archived')}
          aria-label="Include archived actions"
        >
          <span className="stat-label">Archived</span>
          <span className="stat-value"><AnimatedCounter value={stats.archived} /></span>
        </button>

        <button
          type="button"
          className="stat-item stat-button"
          onClick={() => setOpenssfFilter('above5')}
          aria-label="Show actions with OpenSSF score"
        >
          <span className="stat-label">OpenSSF</span>
          <span className="stat-value"><AnimatedCounter value={stats.withOssf} /></span>
        </button>
      </div>

      <div className="controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, owner, type, or keyword..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <button
            className={typeFilter === 'All' ? 'active' : ''}
            onClick={() => {
              setTypeFilter('All');
            }}
          >
            All
          </button>
          <button
            className={typeFilter === 'Node' ? 'active' : ''}
            onClick={() => {
              setTypeFilter(typeFilter === 'Node' ? 'All' : 'Node');
            }}
          >
            Node/JS
          </button>
          <button
            className={typeFilter === 'Docker' ? 'active' : ''}
            onClick={() => {
              setTypeFilter(typeFilter === 'Docker' ? 'All' : 'Docker');
            }}
          >
            Docker
          </button>
          <button
            className={typeFilter === 'Composite' ? 'active' : ''}
            onClick={() => {
              setTypeFilter(typeFilter === 'Composite' ? 'All' : 'Composite');
            }}
          >
            Composite
          </button>
          <button
            className={typeFilter === 'Unknown' ? 'active' : ''}
            onClick={() => {
              setTypeFilter(typeFilter === 'Unknown' ? 'All' : 'Unknown');
            }}
          >
            Unknown
          </button>
          <button
            className={typeFilter === 'No file found' ? 'active' : ''}
            onClick={() => {
              setTypeFilter(typeFilter === 'No file found' ? 'All' : 'No file found');
            }}
          >
            No file found
          </button>
        </div>

        <div className="controls-row">
          <div className="filter-group">
            <label>Verified:</label>
            <select data-testid="filter-verified" value={verifiedFilter} onChange={e => setVerifiedFilter(e.target.value as any)}>
              <option value="all">All</option>
              <option value="verified">Verified only</option>
              <option value="unverified">Only unverified</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Archived:</label>
            <select data-testid="filter-archived" value={archivedFilter} onChange={e => setArchivedFilter(e.target.value as any)}>
              <option value="hide">Hide archived</option>
              <option value="show">Show archived</option>
              <option value="only">Only archived</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Low-activity:</label>
            <select data-testid="filter-activity" value={activityFilter} onChange={e => setActivityFilter(e.target.value as any)}>
              <option value="hide">Hide low-activity</option>
              <option value="show">Show all</option>
              <option value="only">Only low-activity</option>
            </select>
          </div>

          <div className="filter-group">
            <label>OpenSSF score:</label>
            <select data-testid="filter-ossf" value={openssfFilter} onChange={e => setOpenssfFilter(e.target.value as any)}>
              <option value="all">All scores</option>
              <option value="above5">Above 5</option>
              <option value="above7">Above 7</option>
            </select>
          </div>

          <div className="filter-group">
            <label>GitHub-owned:</label>
            <select data-testid="filter-github-owned" value={githubOwnedFilter} onChange={e => setGithubOwnedFilter(e.target.value as any)}>
              <option value="all">All</option>
              <option value="only">Only GitHub-owned</option>
              <option value="hide">Hide GitHub-owned</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sort by:</label>
            <button data-testid="sort-updated"
              className={sortBy === 'updated' ? 'active' : ''}
              onClick={() => setSortBy('updated')}
            >
              Last Updated
            </button>
            <button data-testid="sort-dependents"
              className={sortBy === 'dependents' ? 'active' : ''}
              onClick={() => setSortBy('dependents')}
            >
              Used by
            </button>
          </div>
        </div>
      </div>

      {filteredActions.length === 0 ? (
        <div className="no-results">
          <h2>No actions found</h2>
          <p>
            Try adjusting your search or filters. ({actions.length.toLocaleString()} loaded,{' '}
            {filteredActions.length.toLocaleString()} matching)
          </p>
          <button
            type="button"
            className="back-button"
            onClick={clearFilters}
            style={{ marginTop: '12px' }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div className="actions-grid">
            {pagedActions.map(action => (
              <a
                key={`${action.owner}/${action.name}`}
                className={`action-card ${action.repoInfo?.archived ? 'archived' : ''}`}
                href={`/action/${encodeURIComponent(action.owner)}/${encodeURIComponent(action.name)}`}
                onClick={e => handleActionClick(action, e)}
              >
                <div className="action-header">
                  <div className="action-title">
                      <div className="action-owner">
                        {action.owner}
                        {isGitHubOwnedAction(action) && <GitHubOwnedBadge />}
                      </div>
                      <div className="action-name">{normalizeRepoName(action.owner, action.name)}</div>
                  </div>
                  <span
                    className={`action-badge ${getActionTypeBadgeClass(
                      action?.actionType?.actionType
                    )}`}
                  >
                    {action?.actionType?.actionType || 'Unknown'}
                  </span>
                </div>

                <div className="action-meta">
                  <div className="meta-item">
                    <span>👥</span>
                    <strong className="dependents-highlight">
                      {formatDependentsCount(action?.dependents?.dependents)}
                    </strong>
                    <span>Used by</span>
                  </div>
                  {action.verified && (
                    <div className="meta-item">
                      <span>✓</span>
                      <span>Verified</span>
                    </div>
                  )}
                  {/* Show OpenSSF score if present */}
                  {typeof action.ossfScore === 'number' && (
                    <div className="meta-item">
                      <span>🔒</span>
                      <span>OpenSSF: <strong>{action.ossfScore.toFixed(1)}</strong></span>
                    </div>
                  )}
                  {action.repoInfo?.archived && (
                    <div className="meta-item">
                      <span>📦</span>
                      <span>Archived</span>
                    </div>
                  )}
                </div>

                <div className="action-meta">
                  {action.releaseInfo && action.releaseInfo.length > 0 && (
                    <div className="meta-item">
                      <span>🏷️</span>
                      <span>Latest: {action.releaseInfo[0]}</span>
                    </div>
                  )}
                  <div className="meta-item">
                    <span>🕐</span>
                    <span>
                      Updated:{' '}
                      {action?.repoInfo?.updated_at
                        ? new Date(action.repoInfo.updated_at).toLocaleDateString()
                        : 'Unknown'}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div className="pagination">
            <div className="pagination-summary">
              Showing {showingFrom}-{showingTo} of {filteredActions.length.toLocaleString()} matching
              {hasActiveFilters && (
                <>
                  {' · '}
                  <span className="pagination-hidden">
                    {hiddenByFilters.toLocaleString()} hidden by filters
                  </span>
                  {' · '}
                  {actions.length.toLocaleString()} total
                  {' · '}
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Reset filters
                  </button>
                </>
              )}
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-page">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
